import { io, Socket } from 'socket.io-client';
import Cookies from 'js-cookie';

export interface ImprovementStatus {
  id: string;
  status: 'in_progress' | 'completed' | 'failed';
  total: number;
  succeeded: number;
  failed: number;
  progress: number;
  result: unknown | null;
  createdAt: string;
  updatedAt: string;
}

type ConnectionStatusHandler = (isConnected: boolean) => void;
type ImprovementStatusHandler = (status: ImprovementStatus) => void;
type ImprovementCompletedHandler = (status: ImprovementStatus) => void;
type ImprovementStartedHandler = (status: ImprovementStatus) => void;
type ImprovementErrorHandler = (error: { message: string }) => void;
type SyncErrorHandler = (error: unknown) => void;

const SOCKET_CONFIG = {
  path: '/socket.io',
  reconnectionAttempts: 5,
  reconnectionDelay: 1000,
  reconnectionDelayMax: 5000,
  timeout: 10000,
  transports: ['websocket'] as const,
} as const;

export const createProductSyncService = () => {
  let socket: Socket | null = null;
  let onConnectionStatusChange: ConnectionStatusHandler | null = null;
  let onImprovementStatus: ImprovementStatusHandler | null = null;
  let onImprovementCompleted: ImprovementCompletedHandler | null = null;
  let onImprovementStarted: ImprovementStartedHandler | null = null;
  let onImprovementError: ImprovementErrorHandler | null = null;
  let onSyncError: SyncErrorHandler | null = null;
  let isConnectedState = false;

  const attachListeners = () => {
    if (!socket) return;

    socket.on('connect', () => {
      console.info('✅ Product sync socket connected:');
      isConnectedState = true;
      onConnectionStatusChange?.(true);
    });

    socket.on('disconnect', () => {
      isConnectedState = false;
      onConnectionStatusChange?.(false);
    });

    socket.on('connect_error', (err: Error) => {
      isConnectedState = false;
      onConnectionStatusChange?.(false);
      onSyncError?.(err);
    });

    socket.on('improvementStarted', (data: unknown) => {
      console.log('improvementStarted', data);
      const status = data as ImprovementStatus;
      onImprovementStarted?.(status);
    });

    socket.on('improvementProgress', (data: unknown) => {
      console.log('improvementProgress', data);
      const status = data as ImprovementStatus;
      onImprovementStatus?.(status);
    });

    socket.on('improvementCompleted', (data: unknown) => {
      const status = data as ImprovementStatus;
      onImprovementCompleted?.(status);
    });

    socket.on('improvementError', (data: unknown) => {
      const error = data as { message: string };
      onImprovementError?.(error);
    });

    socket.on('authenticated', () => {});

    socket.on('error', (data: unknown) => {
      onSyncError?.(data);
    });
  };

  const connectSocket = (
    url: string,
    handlers: {
      onConnectionStatusChange?: ConnectionStatusHandler;
      onImprovementStatus?: ImprovementStatusHandler;
      onImprovementCompleted?: ImprovementCompletedHandler;
      onImprovementStarted?: ImprovementStartedHandler;
      onImprovementError?: ImprovementErrorHandler;
      onSyncError?: SyncErrorHandler;
    }
  ) => {
    if (socket && socket.connected) {
      return;
    }

    if (socket) {
      socket.disconnect();
      socket.removeAllListeners();
      socket = null;
    }

    const token = Cookies.get('authToken');
    if (!token) {
      onSyncError?.(new Error('No authentication token found'));
      return;
    }

    socket = io(`${url}/product`, {
      ...SOCKET_CONFIG,
      auth: {
        token: `Bearer ${token}`,
      },
      extraHeaders: {
        Authorization: `Bearer ${token}`,
      },
      transports: ['websocket'],
    });

    onConnectionStatusChange = handlers.onConnectionStatusChange || null;
    onImprovementStatus = handlers.onImprovementStatus || null;
    onImprovementCompleted = handlers.onImprovementCompleted || null;
    onImprovementStarted = handlers.onImprovementStarted || null;
    onImprovementError = handlers.onImprovementError || null;
    onSyncError = handlers.onSyncError || null;

    attachListeners();
  };

  const disconnectSocket = () => {
    if (!socket) return;
    socket.disconnect();
    socket.removeAllListeners();
    socket = null;
    isConnectedState = false;
    onConnectionStatusChange?.(false);
  };

  const startImprovement = (): Promise<void> => {
    if (!socket?.connected) {
      return Promise.reject(new Error('Socket not connected'));
    }

    return new Promise((resolve, reject) => {
      let resolved = false;

      socket?.emit('startImprovement', {}, (response: unknown) => {
        if (resolved) return;
        resolved = true;

        if (!response) {
          return reject(new Error('No response received from server'));
        }
        const responseObj = response as { error?: string; success?: boolean };
        if (typeof responseObj !== 'object' || responseObj === null) {
          return reject(new Error('Invalid response format from server'));
        }
        if (responseObj?.error) {
          reject(new Error(responseObj.error));
        } else {
          resolve();
        }
      });
    });
  };

  return {
    connectSocket,
    disconnectSocket,
    startImprovement,
    isConnected: () => Boolean(socket?.connected) || isConnectedState,
  };
};

export type ProductSyncService = ReturnType<typeof createProductSyncService>;
